import React from 'react'
import {
  Container,
  Backdrop,
  CircularProgress,
  Typography,
  Button,
} from '@mui/material'
import { ActivationStatusBlock } from './blocks/ActivationStatusBlock/ActivationStatusBlock'
import { CustomerBlock } from './blocks/CustomerBlock'
import { QualificationBlock } from './blocks/QualificationBlock/QualificationBlock'
import { RecordUpdateBlock } from './blocks/QualificationBlock/RecordUpdateBlock'
import { VehicleBlock } from './blocks/VehicleBlock'
import { ProgramPricingBlock } from './blocks/ProgramPricingBlock/ProgramPricingBlock'
import { CancellationBlock } from './blocks/CancellationBlock/CancellationBlock'

import { useReservation } from './useReservation'
import { ActivityState } from '@nxcr-org/web-api-interface/lib/domain_pb'

import { BackButton } from '../../../../components/user-navigation/BackButton'
import { Article } from 'shared/Article'
import { Section } from 'shared/Section'
import { ChangeProgramDialog } from './blocks/ChangeProgramDialog/ChangeProgramDialog'
import { AppointmentBlock } from './blocks/AppointmentBlock/AppointmentBlock'
import { AssignmentBlock } from './blocks/AssignmentBlock/AssignmentBlock'
import { InsuranceBlock } from './blocks/InsuranceBlock/InsuranceBlock'
import InsuranceQuoteBlock from './blocks/InsuranceQuoteBlock/InsuranceQuoteBlock'
import { useTheme } from '../../../../utils/themeContext'
import { getColorsForTheme } from '../../../../global-styles/clientColors'
import { RelatedAccounts } from './blocks/RelatedAccounts'

const ReservationPage: React.FC = () => {
  const {
    reservation,
    isAdmin,
    overrideQualification,
    overrideQualificationSuccess,
    mostRecentNote,
    currentNote,
    setCurrentNote,
    recalculateCustomerScores,
    recalculateCustomerScoresSuccess,
    setReload,
    assignVehicle,
    unassignVehicle,
    addNote,
    cancelReservation,
    cancelError,
    isLoading,
    reservationError,
    toggleChangeProgram,
    changeProgramOpen,
    cqeScores,
    disqualificationVendor,
    overrideDisqualificationVendor,
    setOverrideDisqualificationVendor,
  } = useReservation()

  const { currentTheme } = useTheme()
  const themeColors = getColorsForTheme(currentTheme)

  return (
    <Container maxWidth={false}>
      <Backdrop
        open={isLoading}
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress />
      </Backdrop>
      <BackButton lastPageRoute="/fleet/reservations" />
      <Article
        heading={
          <Typography variant="h1">
            Reservation Details /{' '}
            <em>{reservation?.assignedVehicle?.model || ''}</em>
          </Typography>
        }
        headerHasBottomBorder={true}
        actionBlock={
          <Button
            onClick={toggleChangeProgram}
            variant="contained"
            sx={{
              backgroundColor: themeColors.buttonColor,
              color: currentTheme === 'galpin' ? 'black' : 'white',
              '&:hover': {
                backgroundColor: themeColors.buttonHover,
              },
            }}
          >
            Switch Programs
          </Button>
        }
      >
        {reservationError ? (
          <Typography>{reservationError['message']}</Typography>
        ) : (
          <>
            {/*todo: make this more dynamic especially if more clients are added*/}
            <Section heading={ reservation.customer.clientId == '613e89a2-2a57-4050-9b7e-eda0a1bf94a7' ? 'Galpin Customer' : 'Autonomy Customer' }>
              <CustomerBlock {...reservation} />
            </Section>
            {/*todo: replace this with customer-details version after convert to Galpin is no longer needed so there are not two different variations of the same component*/}
              <RelatedAccounts reservation={reservation} />
            <Section heading="Vehicle">
              <VehicleBlock {...reservation.vehicle} />
            </Section>
            <Section heading="Activation status">
              <ActivationStatusBlock
                currentActivity={reservation.customerInfo.currentActivity}
                currentStatus={reservation.status}
                isQualified={reservation.isQualified}
                customerId={reservation?.customerId}
                subscriptionId={reservation?.subscriptionId}
              />
            </Section>
            {reservation?.customerInfo?.accountNumber && (
              <AppointmentBlock reservation={reservation} />
            )}
            <QualificationBlock />
            {(reservation.qualifications?.hasQualifications || isAdmin) && (
              <Section heading="Update Record" subSection>
                <RecordUpdateBlock
                  currentTier={reservation.customerInfo.qualificationTier}
                  overrideQualification={overrideQualification}
                  overrideQualificationSuccess={overrideQualificationSuccess}
                  note={currentNote}
                  setNote={setCurrentNote}
                  mostRecentNote={mostRecentNote}
                  addNote={addNote}
                  customer={reservation?.customer}
                  recalculateCustomerScores={recalculateCustomerScores}
                  recalculateCustomerScoresSuccess={
                    recalculateCustomerScoresSuccess
                  }
                  isAdmin={isAdmin}
                  cqeScores={cqeScores}
                  disqualificationVendor={disqualificationVendor}
                  overrideDisqualificationVendor={
                    overrideDisqualificationVendor
                  }
                  setOverrideDisqualificationVendor={
                    setOverrideDisqualificationVendor
                  }
                />
              </Section>
            )}
            <ProgramPricingBlock
              reservation={reservation}
              isAdmin={isAdmin}
              setReload={setReload}
            />
            <Section heading="Reservation Assignment">
              <AssignmentBlock
                customerTier={reservation.customerInfo.qualificationTier}
                reload={false}
                reservationLoading={isLoading}
                assignVehicle={(vehicleId) => {
                  assignVehicle(vehicleId)
                }}
                assignedVehicle={reservation?.assignedVehicle}
                unAssignVehicle={unassignVehicle}
                reservationStatus={reservation?.reservationStatus}
                make={reservation?.make}
                programId={reservation?.programId}
                vehicleId={reservation?.vehicleId}
              />
            </Section>
            <Section heading="Insurance">
              <InsuranceBlock
                subscriptionId={reservation?.subscriptionId}
                customerId={reservation?.customerId}
              />
            </Section>
            <Section heading="Insurance Quote">
              {reservation.subscriptionId ? (
                <InsuranceQuoteBlock
                  subscriptionId={reservation?.subscriptionId}
                  customerId={reservation?.customer?.id}
                />
              ) : null}
            </Section>
            {reservation.customer.activityState !==
              ActivityState.SUBSCRIPTION_ACTIVATED && (
              <Section heading="Cancel Order">
                <CancellationBlock
                  isCancelled={reservation.isCancelled}
                  cancelSubscription={cancelReservation}
                  errorCancelling={cancelError}
                />
              </Section>
            )}
          </>
        )}
        {changeProgramOpen && (
          // replaced current program with programId
          <ChangeProgramDialog
            isOpen={changeProgramOpen}
            onClose={toggleChangeProgram}
            programId={reservation?.programId}
            customerId={reservation?.customer?.id}
            reservation={reservation?.reservation}
            postal={reservation?.reservation.postal}
          />
        )}
      </Article>
    </Container>
  )
}

export default ReservationPage
